import { Search, Calendar, FileText, Download, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format, subYears } from "date-fns";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface PaymentHeaderProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  dateRange: { from: Date | undefined; to: Date | undefined };
  onDateRangeChange: (range: {
    from: Date | undefined;
    to: Date | undefined;
  }) => void;
}

export function PaymentHeader({
  searchQuery,
  onSearchChange,
  dateRange,
  onDateRangeChange,
}: PaymentHeaderProps) {
  // Calculate date limits
  const today = new Date();
  const threeYearsAgo = subYears(today, 3);

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header Section with Card */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold text-foreground">
            Payment History
          </h1>
          <p className="text-sm text-muted-foreground">
            View your payment history and download invoices
          </p>
        </div>

        {/* Quick Action Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-card text-card-foreground rounded-lg p-4 border shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Recent Payments</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  View your latest transactions
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary" />
              </div>
            </div>
          </div>

          <div className="bg-card text-card-foreground rounded-lg p-4 border shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Download Invoices</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Get PDF copies of your invoices
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Download className="h-5 w-5 text-primary" />
              </div>
            </div>
          </div>

          <div className="bg-card text-card-foreground rounded-lg p-4 border shadow-sm hover:shadow-md transition-shadow sm:col-span-2 lg:col-span-1">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Payment Status</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Track your payment status
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <RefreshCw className="h-5 w-5 text-primary" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="flex flex-col sm:flex-row gap-3 bg-card/50 p-4 rounded-lg border">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            type="text"
            placeholder="Search by invoice ID or description..."
            className="w-full pl-10 pr-4 py-2 h-10 bg-background"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        {/* Date Range Picker */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full sm:w-[240px] h-10 justify-start text-left font-normal",
                dateRange.from && "text-foreground"
              )}
            >
              <Calendar className="mr-2 h-4 w-4 shrink-0" />
              <span className="truncate">
                {dateRange.from ? (
                  dateRange.to ? (
                    <>
                      {format(dateRange.from, "MMM d, yyyy")} -{" "}
                      {format(dateRange.to, "MMM d, yyyy")}
                    </>
                  ) : (
                    format(dateRange.from, "MMM d, yyyy")
                  )
                ) : (
                  "Pick a date range"
                )}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <CalendarComponent
              initialFocus
              mode="range"
              defaultMonth={dateRange.from}
              selected={{
                from: dateRange.from,
                to: dateRange.to,
              }}
              onSelect={(range) => {
                onDateRangeChange({
                  from: range?.from,
                  to: range?.to,
                });
              }}
              disabled={
                (date) =>
                  date > today || // Disable future dates
                  date < threeYearsAgo // Disable dates more than 3 years ago
              }
              numberOfMonths={2}
              className="rounded-md border"
            />
          </PopoverContent>
        </Popover>

        {/* Clear Filters Button */}
        {(dateRange.from || searchQuery) && (
          <Button
            variant="ghost"
            className="mt-2 sm:mt-0"
            onClick={() => {
              onDateRangeChange({ from: undefined, to: undefined });
              onSearchChange("");
            }}
          >
            Clear Filters
          </Button>
        )}
      </div>
    </div>
  );
}
